import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import {
  BuyerHeaders,
  CartLine,
  CreateOrderBody,
  CreateOrderResponse,
  OrderView,
  PaymentMethod,
  PlatformBrand,
  PlatformCatalogPage,
  PlatformListingDetail,
  QuoteView,
  SortKey,
} from './types';

// CRA proxy → marketplace PLATFORM (REACT_APP_MARKETPLACE_API_URL). The
// platform is a separate service from the Sandoog BFF. See setupProxy.js.
const MARKETPLACE_API = '/marketplace-api';
const CATALOG_URL = `${MARKETPLACE_API}/catalog`;
const BRANDS_URL = `${MARKETPLACE_API}/catalog/brands`;
const ORDERS_URL = `${MARKETPLACE_API}/orders`;
const QUOTE_URL = `${ORDERS_URL}/quote`;

// Freight doesn't depend on payment method, so the quote uses a safe default
// (matches the checkout default + the first PaymentMethod enum value).
const QUOTE_PAYMENT_METHOD: PaymentMethod = 'wallet';

const buyerHeaders = (buyer: BuyerHeaders) => ({
  'x-buyer-user-id': buyer.buyerUserId,
  ...(buyer.buyerClientId ? { 'x-buyer-client-id': buyer.buyerClientId } : {}),
});

interface UseCatalogParams {
  q?: string;
  category?: string;
  brand?: string;
  sort?: SortKey;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}

// GET /catalog — published listings only (enforced server-side).
export const useCatalog = ({
  enabled = true,
  category,
  ...rest
}: UseCatalogParams = {}) => {
  // 'All' is a client-side label, not a server filter.
  const params = {
    ...rest,
    ...(category && category !== 'All' ? { category } : {}),
  };
  const { data, isLoading, isError, refetch } = useQuery<PlatformCatalogPage>(
    [CATALOG_URL, params],
    async () => {
      const { data: res } = await axios.get<PlatformCatalogPage>(CATALOG_URL, {
        params,
      });
      return res;
    },
    { enabled, keepPreviousData: true }
  );

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    isLoading,
    isError,
    refetch,
  };
};

// GET /catalog/brands.
export const useBrands = (enabled: boolean) => {
  const { data, isLoading, isError } = useQuery<PlatformBrand[]>(
    [BRANDS_URL],
    async () => {
      const { data: res } = await axios.get<PlatformBrand[]>(BRANDS_URL);
      return res;
    },
    { enabled }
  );
  return { brands: data ?? [], isLoading, isError };
};

// GET /catalog/:id — full listing + supplier.
export const useCatalogDetail = (id: string | null, enabled: boolean) => {
  const { data, isLoading, isError } = useQuery<PlatformListingDetail>(
    [CATALOG_URL, 'detail', id],
    async () => {
      const { data: res } = await axios.get<PlatformListingDetail>(
        `${CATALOG_URL}/${id}`
      );
      return res;
    },
    { enabled: enabled && !!id }
  );
  return { listing: data, isLoading, isError };
};

// POST /orders — buyer identity in headers (x-buyer-*), NOT the body.
// Returns one OrderView PER MERCHANT (a multi-merchant cart splits into POs).
export const useCreateOrders = (buyer: BuyerHeaders) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading, isError, reset } = useMutation(
    async (body: CreateOrderBody): Promise<CreateOrderResponse> => {
      const { data } = await axios.post<CreateOrderResponse>(ORDERS_URL, body, {
        headers: buyerHeaders(buyer),
      });
      return data;
    },
    {
      // Global staleTime is Infinity and the My Orders observer never
      // remounts (only its `enabled` flag toggles), so without an explicit
      // invalidation a just-placed PO stays invisible until a hard reload.
      onSuccess: () => {
        queryClient.invalidateQueries([ORDERS_URL, 'my', buyer.buyerUserId]);
      },
    }
  );
  return { createOrders: mutateAsync, isPlacing: isLoading, isError, reset };
};

// GET /orders — buyer's own POs, newest-first.
// Buyer identity is x-buyer-user-id (same pattern as POST /orders / POST /orders/quote).
export const useMyOrders = (buyer: BuyerHeaders, enabled: boolean) => {
  const { data, isLoading, isError, refetch } = useQuery<OrderView[]>(
    [ORDERS_URL, 'my', buyer.buyerUserId],
    async () => {
      const { data: res } = await axios.get<OrderView[]>(ORDERS_URL, {
        headers: buyerHeaders(buyer),
      });
      return res;
    },
    { enabled: enabled && !!buyer.buyerUserId }
  );
  return { orders: data ?? [], isLoading, isError, refetch };
};

// POST /orders/quote — SERVER-OWNED freight preview for the current cart.
// No order created, no stock touched. Keyed on the cart contents so it
// refetches whenever a qty or item changes; keepPreviousData holds the prior
// freight visible across that refetch (and across the cart→checkout hop).
export const useQuote = (lines: CartLine[], enabled: boolean) => {
  const items = lines.map(({ product, qty }) => ({ listingId: product.id, qty }));
  const { data, isLoading, isError, isFetching } = useQuery<QuoteView>(
    [QUOTE_URL, items],
    async () => {
      const { data: res } = await axios.post<QuoteView>(QUOTE_URL, {
        items,
        paymentMethod: QUOTE_PAYMENT_METHOD,
      });
      return res;
    },
    { enabled: enabled && items.length > 0, keepPreviousData: true }
  );
  return { quote: data, isLoading, isError, isFetching };
};
